import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight, ArrowDown, Orbit, Star } from "lucide-react";
import EcosystemCanvas from "@/components/EcosystemCanvas";
import { useTours } from "@/data/useTours";

const MaskedLine = ({ children, delay }) => {
  const reduce = useReducedMotion();
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        className="block"
        initial={reduce ? false : { y: "110%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
};

const Hero = () => {
  const reduce = useReducedMotion();
  const tours = useTours();
  return (
    <section data-testid="hero-section" className="relative min-h-[100svh] flex flex-col bg-navy-deep grain overflow-hidden">
      <div className="absolute inset-0 dot-grid opacity-25" aria-hidden="true" />
      <EcosystemCanvas className="absolute inset-0 w-full h-full" />
      <div className="absolute -top-32 -left-32 w-[480px] h-[480px] bg-brand/25 blur-[140px] rounded-full" aria-hidden="true" />
      <div className="absolute bottom-0 right-0 w-[520px] h-[420px] bg-electric/10 blur-[150px] rounded-full" aria-hidden="true" />

      <div className="relative flex-1 flex items-center max-w-8xl mx-auto w-full px-6 lg:px-12 pt-32 pb-20">
        <div className="max-w-4xl">
          <motion.div
            initial={reduce ? false : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="inline-flex items-center gap-3 border border-white/10 bg-white/5 backdrop-blur-md rounded-full px-4 py-2 mb-4"
          >
            <span className="w-2 h-2 rounded-full bg-electric animate-pulse" />
            <span className="text-xs md:text-sm tracking-[0.2em] uppercase text-mist/80">
              Click World Online — Strategy That Clicks
            </span>
          </motion.div>

          <motion.p
            initial={reduce ? false : { opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            data-testid="hero-since-2017"
            className="flex items-center gap-2.5 text-xs md:text-sm tracking-[0.3em] uppercase text-mist/50 mb-8"
          >
            <span className="w-8 h-px bg-electric/60" aria-hidden="true" />
            Since 2017
          </motion.p>

          <h1 className="font-display font-black tracking-tighter leading-[0.98] text-[13vw] sm:text-6xl md:text-7xl lg:text-[6.5rem] text-mist">
            <MaskedLine delay={0.25}>
              WE MAKE BRANDS <span className="text-electric">CLICK.</span>
            </MaskedLine>
            <MaskedLine delay={0.45}>
              <span className="text-outline-electric">ONLINE &amp; BEYOND.</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={reduce ? false : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75 }}
            className="mt-8 text-base md:text-xl text-slatec max-w-xl leading-relaxed"
          >
            Click World Online helps businesses strengthen their digital presence through strategy, creative content,
            social media, SEO, advertising, lead generation and modern digital experiences.
          </motion.p>

          <motion.div
            initial={reduce ? false : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => document.getElementById("client-reviews")?.scrollIntoView({ behavior: "smooth" })}
              data-testid="hero-reviews-cta"
              className="inline-flex items-center gap-2 border border-[#FBBF24]/40 bg-[#FBBF24]/10 text-mist font-semibold px-6 py-4 rounded-full hover:bg-[#FBBF24]/20 transition-colors duration-300"
            >
              <Star size={16} className="text-[#FBBF24]" fill="#FBBF24" />
              4.7 · Google Reviews
            </button>
            <Link
              to="/contact"
              data-testid="hero-primary-cta"
              className="inline-flex items-center gap-2 bg-brand hover:bg-electric hover:text-navy-deep text-white font-semibold px-8 py-4 rounded-full transition-colors duration-300"
            >
              Let's Grow Your Brand
              <ArrowUpRight size={18} />
            </Link>
            <Link
              to="/services"
              data-testid="hero-secondary-cta"
              className="inline-flex items-center gap-2 border border-white/20 text-mist hover:border-electric hover:text-electric font-semibold px-8 py-4 rounded-full transition-colors duration-300"
            >
              Explore Our Services
            </Link>
          </motion.div>
        </div>

        <motion.div
          initial={reduce ? false : { opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 1, delay: 1.05 }}
          className="hidden xl:block absolute right-12 top-1/2 -translate-y-1/2"
        >
          <div className="relative animate-float-soft">
            <div className="absolute -inset-6 border border-electric/15 rounded-full animate-spin-slow" aria-hidden="true" />
            <Link
              to="/360-experiences"
              data-testid="hero-360-badge"
              className="relative flex flex-col items-center gap-3 bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl px-8 py-8 hover:border-electric/40 transition-colors duration-300"
            >
              <span className="bg-white rounded-2xl p-3">
                <img src="/assets/logo.png" alt="Click World Online" className="h-16 w-auto" />
              </span>
              <span className="flex items-center gap-2 text-electric text-sm font-semibold">
                <Orbit size={16} />
                {tours.length ? tours.length : "400"}+ 360° tours published
              </span>
              <span className="text-mist/50 text-xs tracking-wide">Explore immersive experiences</span>
            </Link>
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={reduce ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.8 }}
        className="relative max-w-8xl mx-auto w-full px-6 lg:px-12 pb-10 flex items-center justify-between"
      >
        <span className="flex items-center gap-3 text-mist/40 text-xs tracking-[0.25em] uppercase">
          Scroll
          <ArrowDown size={14} className="animate-bounce" />
        </span>
        <span className="text-mist/40 text-xs tracking-[0.25em] uppercase hidden sm:block">
          Strategy · Creativity · Technology · Performance
        </span>
      </motion.div>
    </section>
  );
};

export default Hero;
